import { hc } from "hono/client";
import type { z } from "@hono/zod-openapi";
import type { ApiType } from "@admin-api/routes";
import type {
  AdminMysqlTablesResponse as AdminMysqlTablesResponseSchema,
  AdminReconcileResponse as AdminReconcileResponseSchema,
  AdminReconcileRefreshResponse as AdminReconcileRefreshResponseSchema,
  AdminSlackJobsResponse as AdminSlackJobsResponseSchema,
  AdminSlackQueueResponse as AdminSlackQueueResponseSchema,
  AdminOpsIncidentsResponse as AdminOpsIncidentsResponseSchema,
  AdminOpsIncidentDetailResponse as AdminOpsIncidentDetailResponseSchema,
  OpsIncidentSource,
  OpsIncidentSeverity,
  OpsIncidentStatus,
  ReconcileProvider,
  ReconcileSeverity,
} from "@admin-api/_lib/contracts/ops";
import type {
  AdminSpendAggregateResponse as AdminSpendAggregateResponseSchema,
  AdminSpendUserResponse as AdminSpendUserResponseSchema,
} from "@admin-api/_lib/contracts/spend";
import type {
  AdminCreditGrantsResponse as AdminCreditGrantsResponseSchema,
  AdminRevenueAggregateResponse as AdminRevenueAggregateResponseSchema,
  AdminRevenuePaymentsResponse as AdminRevenuePaymentsResponseSchema,
  AdminSubscriptionAuditResponse as AdminSubscriptionAuditResponseSchema,
  AdminUserRevenueResponse as AdminUserRevenueResponseSchema,
} from "@admin-api/_lib/contracts/revenue";
import type {
  AdminConversationListResponse as AdminConversationListResponseSchema,
  AdminConversationDetailResponse as AdminConversationDetailResponseSchema,
  AdminConversationSessionSummary as AdminConversationSessionSummarySchema,
  AdminConversationMessage as AdminConversationMessageSchema,
} from "@admin-api/_lib/contracts/conversations";

export class ApiRequestError extends Error {
  constructor(
    message: string,
    public readonly code: string,
    public readonly status: number,
  ) {
    super(message);
    this.name = "ApiRequestError";
  }
}

/** Heroku H12 / gateway HTML error pages are not useful in the ops UI. */
export function sanitizeApiErrorMessage(text: string, fallbackStatusText?: string): string {
  const trimmed = text.trim();
  if (
    trimmed.startsWith("<!DOCTYPE") ||
    trimmed.startsWith("<html") ||
    trimmed.includes("herokucdn.com/error-pages") ||
    trimmed.includes("Application Error")
  ) {
    return "Request timed out. The action may still be running - refresh to check status.";
  }
  return trimmed || fallbackStatusText || "request failed";
}

export function isLikelyRequestTimeoutMessage(message: string): boolean {
  return (
    message.includes("timed out") ||
    message.includes("may still be running") ||
    message.includes("<!DOCTYPE") ||
    message.includes("herokucdn.com/error-pages") ||
    message.includes("Application Error")
  );
}

const rpc = hc<ApiType>("/api", {
  fetch(input: RequestInfo | URL, init?: RequestInit) {
    return fetch(input, { ...init, credentials: "same-origin" });
  },
});

async function parseJson<T>(res: Response): Promise<T> {
  const text = await res.text();
  let body: T | { error?: { code: string; message: string } };
  try {
    body = JSON.parse(text) as T | { error?: { code: string; message: string } };
  } catch {
    if (!res.ok) {
      throw new ApiRequestError(
        sanitizeApiErrorMessage(text || res.statusText, res.statusText),
        "request_failed",
        res.status,
      );
    }
    throw new ApiRequestError("Invalid JSON response", "invalid_response", res.status);
  }
  if (!res.ok) {
    const err = body as { error?: { code: string; message: string } };
    throw new ApiRequestError(
      sanitizeApiErrorMessage(err.error?.message ?? res.statusText, res.statusText),
      err.error?.code ?? "request_failed",
      res.status,
    );
  }
  return body as T;
}

export type AdminMe = { id: string; email: string };

export async function getMe(): Promise<AdminMe> {
  const res = await rpc.auth.me.$get();
  return parseJson(await res);
}

export async function login(email: string, password: string): Promise<AdminMe> {
  const res = await rpc.auth.login.$post({ json: { email, password } });
  return parseJson(await res);
}

export async function logout(): Promise<void> {
  const res = await rpc.auth.logout.$post();
  await parseJson(await res);
}

export type Stats = {
  userCount: number;
  runningVmCount: number;
  errorVmCount: number;
  pendingContactCount: number;
  slackQueuePending: number;
  slackQueueProcessing: number;
  openIncidentCount: number;
  openCriticalIncidentCount: number;
  hibernatedVmCount: number;
  wakingVmCount: number;
};

export async function getStats(): Promise<Stats> {
  const res = await rpc.stats.$get();
  return parseJson(await res);
}

export type UserSummary = {
  id: string;
  email: string;
  createdAt: string;
  serverStatus: string | null;
  spendUsdCents: number;
  remainingCredits: number;
  platformRevenueCents: number;
  earnedCredits: number;
  signupCountry: string | null;
  signupCity: string | null;
  signupTimezone: string | null;
  planCode: string | null;
  subscriptionStatus: string | null;
};

export type UserList = {
  items: UserSummary[];
  total: number;
  limit: number;
  offset: number;
};

export async function getUsers(limit = 50, offset = 0): Promise<UserList> {
  const res = await rpc.users.$get({
    query: {
      limit: String(limit),
      offset: String(offset),
    },
  });
  return parseJson(await res);
}

export type UserDetail = {
  id: string;
  email: string;
  createdAt: string;
  signupIp: string | null;
  signupCountry: string | null;
  signupGeo: Record<string, string | undefined> | null;
  signupClient: Record<string, string | number | undefined> | null;
  agentConfig: {
    timezone: string;
    locale: string;
    responseStyle: string;
    freeModel: boolean;
    modelMapJson: string;
    personaMd: string;
    updatedAt: string;
  } | null;
  server: {
    id: string;
    status: string;
    region: string;
    serverType: string;
    publicIp: string | null;
    error: string | null;
    lastPhoneHomeAt: string | null;
    hetznerServerId: number | null;
    hetznerVolumeId: number | null;
    createdAt: string;
  } | null;
  workspaceStorage: {
    hetznerVolumeId: number | null;
    provisionedGb: number | null;
    hetznerStatus: string | null;
    attachedServerId: number | null;
    deleteProtected: boolean | null;
    usedBytes: number | null;
    availableBytes: number | null;
    totalBytes: number | null;
    reportedAt: string | null;
    syncedAt: string | null;
    usageStale: boolean;
  } | null;
  spend: {
    usageCents: number;
    limitCents: number;
    remainingCents: number;
    cachedAt: string;
  } | null;
  bot: {
    id: string;
    botUsername: string;
    botId: number;
    telegramAllowedUsers: string | null;
    isActive: boolean;
    createdAt: string;
    updatedAt: string;
  } | null;
  telegramBots: Array<{
    id: string;
    botUsername: string;
    botId: number;
    telegramAllowedUsers: string | null;
    isActive: boolean;
    createdAt: string;
    updatedAt: string;
  }>;
  telegramPairingSessions: Array<{
    nonce: string;
    suggestedUsername: string;
    suggestedName: string;
    status: "waiting" | "paired" | "expired" | "failed";
    botUsername: string | null;
    botId: number | null;
    creatorTelegramId: number | null;
    createdAt: string;
    expiresAt: string;
    completedAt: string | null;
  }>;
  connections: Array<{
    toolkit: string;
    status: string;
    connectedAt: string | null;
    updatedAt: string;
  }>;
  provisionEvents: Array<{
    id: string;
    kind: string;
    payloadJson: string;
    createdAt: string;
  }>;
  vmBridge: {
    bridgeLastConnectedAt: string | null;
    bridgeLastDisconnectedAt: string | null;
    bridgeDisconnectCount: number;
    liveConnected: boolean;
  } | null;
  slackQueue: {
    pending: number;
    processing: number;
    done: number;
    cancelled: number;
    dead: number;
    oldestPendingAt: string | null;
  } | null;
  slackWorkspace: {
    teamId: string;
    teamName: string | null;
    isActive: boolean;
    createdAt: string;
    updatedAt: string;
  } | null;
  slackWorkspaces: Array<{
    teamId: string;
    teamName: string | null;
    isActive: boolean;
    createdAt: string;
    updatedAt: string;
  }>;
  featureFlags?: {
    allow_data_collection_models: boolean;
    injection_screener_enforce: boolean;
  };
  lastDelegatedWork: {
    at: string | null;
    source: "slack" | "telegram" | "email" | null;
    byChannel: {
      slack?: string;
      telegram?: string;
      email?: string;
    };
    conversationSharingOptIn: boolean;
  };
  hibernation: {
    hibernateWhenIdle: boolean;
    lastHibernateAt: string | null;
    lastWakeAt: string | null;
    lastWakeReason: string | null;
    lastHibernateSkipReason: string | null;
    wakeRequestedAt: string | null;
  };
  closedAt: string | null;
  closedReason: string | null;
  closedBy: string | null;
};

export async function getUser(id: string): Promise<UserDetail> {
  const res = await rpc.users[":id"].$get({ param: { id } });
  return parseJson(await res);
}

export type HibernateEventKind = "hibernated" | "wake_requested" | "awake" | "wake_failed";
export type HibernatePowerState = "running" | "hibernated" | "waking";

export type HibernateHistoryEvent = {
  id: string;
  occurredAt: string;
  kind: HibernateEventKind;
  reason: string | null;
  rebuildJobId: string | null;
  correlationId: string | null;
};

export type HibernateSeriesPoint = {
  t: string;
  state: HibernatePowerState;
  reason: string | null;
};

export type HibernateHistoryResponse = {
  from: string;
  to: string;
  events: HibernateHistoryEvent[];
  series: HibernateSeriesPoint[];
};

export async function getUserHibernateHistory(
  id: string,
  from?: string,
  to?: string,
): Promise<HibernateHistoryResponse> {
  const res = await rpc.users[":id"].workspace["hibernate-history"].$get({
    param: { id },
    query: {
      ...(from ? { from } : {}),
      ...(to ? { to } : {}),
    },
  });
  return parseJson(await res);
}

export type HibernateFleetSeriesPoint = {
  t: string;
  runningCount: number;
  hibernatedCount: number;
  wakingCount: number;
  pausedCount: number;
};

export type HibernateRecentEvent = {
  id: string;
  userId: string;
  email: string;
  occurredAt: string;
  kind: HibernateEventKind;
  reason: string | null;
};

export type HibernateStatsResponse = {
  from: string;
  to: string;
  current: {
    runningCount: number;
    hibernatedCount: number;
    wakingCount: number;
    pausedCount: number;
  };
  series: HibernateFleetSeriesPoint[];
  recentEvents: HibernateRecentEvent[];
};

export async function getHibernateStats(from?: string, to?: string): Promise<HibernateStatsResponse> {
  const res = await rpc.stats.hibernate.$get({
    query: {
      ...(from ? { from } : {}),
      ...(to ? { to } : {}),
    },
  });
  return parseJson(await res);
}

export type WorkspaceStatus = {
  status: string;
  lastPhoneHomeAt: string | null;
  serverType: string | null;
  region: string | null;
  publicIp: string | null;
  needsWorkspaceUpdate: boolean;
  lifecycleError?: "delete_failed" | null;
  integrations: { telegram: boolean; slack: boolean; email: boolean };
  provisioning?: { available: boolean; active: number; cap: number };
};

export type WorkspaceActionResult = { status: string };

export async function createUserWorkspace(
  userId: string,
  conversationSharingOptIn = true,
): Promise<WorkspaceActionResult> {
  const res = await rpc.users[":id"].workspace.create.$post({
    param: { id: userId },
    json: { conversationSharingOptIn },
  });
  return parseJson(await res);
}

export async function deleteUserWorkspace(userId: string): Promise<WorkspaceActionResult> {
  const res = await rpc.users[":id"].workspace.delete.$post({
    param: { id: userId },
    json: {},
  });
  return parseJson(await res);
}

export async function applyConfigUserWorkspace(userId: string): Promise<WorkspaceActionResult> {
  const res = await rpc.users[":id"].workspace["apply-config"].$post({
    param: { id: userId },
    json: {},
  });
  return parseJson(await res);
}

export async function startUserWorkspace(userId: string): Promise<WorkspaceActionResult> {
  const res = await rpc.users[":id"].workspace.start.$post({
    param: { id: userId },
    json: {},
  });
  return parseJson(await res);
}

export async function stopUserWorkspace(userId: string): Promise<WorkspaceActionResult> {
  const res = await rpc.users[":id"].workspace.stop.$post({
    param: { id: userId },
    json: {},
  });
  return parseJson(await res);
}

export async function getUserWorkspaceStatus(userId: string): Promise<WorkspaceStatus> {
  const res = await rpc.users[":id"].workspace.status.$get({
    param: { id: userId },
  });
  return parseJson(await res);
}

export type CloseAccountResult = {
  closedAt: string;
  alreadyClosed: boolean;
  deprovisioned: boolean;
  subscriptionCanceled: boolean;
  openRouterDisabled: boolean;
  sessionsInvalidated: boolean;
};

export async function closeUserAccount(
  userId: string,
  body: { reason: string; confirmEmail: string },
): Promise<CloseAccountResult> {
  const res = await rpc.users[":id"]["close-account"].$post({
    param: { id: userId },
    json: body,
  });
  return parseJson(await res);
}

export type VolumeDeleteResult = { volumeId: number; deleted: boolean };

export async function deleteUserVolume(
  userId: string,
  body: { reason: string; confirmPhrase: "DELETE VOLUME"; confirmEmail: string },
): Promise<VolumeDeleteResult> {
  const res = await rpc.users[":id"].volume.delete.$post({
    param: { id: userId },
    json: body,
  });
  return parseJson(await res);
}

export async function restartUserCoworker(userId: string): Promise<{ ok: true }> {
  const res = await rpc.users[":id"].workspace["restart-coworker"].$post({
    param: { id: userId },
    json: {},
  });
  return parseJson(await res);
}

export type SlackRevokeResult =
  | "revoked"
  | "already_revoked"
  | "decrypt_failed"
  | "slack_error"
  | "network_error";

export interface SlackForceReleaseResult {
  ok: true;
  teamId: string;
  alreadyInactive: boolean;
  /** "uninstall" is the best outcome: it kills every install token at once. */
  method: "uninstall" | "revoke" | "none";
  botRevoked: SlackRevokeResult | null;
  userRevoked: SlackRevokeResult | null;
  jobsCancelled: number | null;
  configBumped: boolean | null;
  purgedJobs: number | null;
  purgedBackfills: number | null;
  purgedCron: number | null;
  composioRevoked: number | null;
}

export async function forceReleaseSlackWorkspace(
  userId: string,
  teamId: string,
): Promise<SlackForceReleaseResult> {
  const res = await rpc.users[":id"].slack["force-release"].$post({
    param: { id: userId },
    json: { teamId },
  });
  return parseJson(await res);
}

export async function patchUserFeatureFlags(
  id: string,
  body: {
    allow_data_collection_models?: boolean;
    injection_screener_enforce?: boolean;
  },
): Promise<{
  allow_data_collection_models: boolean;
  injection_screener_enforce: boolean;
}> {
  const res = await rpc.users[":id"]["feature-flags"].$patch({
    param: { id },
    json: body,
  });
  return parseJson(await res);
}

export type VmLogSourceFilter = "agent" | "errors" | "bootstrap" | "vm_bridge" | "all";

export type VmLogsResponse = {
  userId: string;
  source: VmLogSourceFilter;
  text: string;
  chunkCount: number;
  totalChunks: number;
  limit: number;
  offset: number;
  conversationSharingOptIn: boolean;
  serverStatus: string | null;
  telemetryTokenUpdatedAt: string | null;
  conversationLastSyncAt: string | null;
  conversationLastError: string | null;
  conversationSessionsSynced: number;
  conversationMessagesSynced: number;
  lastSyncAt: string | null;
  lastError: string | null;
  bytesSynced: number;
};

export async function getUserVmLogs(
  userId: string,
  source: VmLogSourceFilter = "all",
  limit = 20,
  offset = 0,
): Promise<VmLogsResponse> {
  const res = await rpc.users[":id"]["vm-logs"].$get({
    param: { id: userId },
    query: { source, limit: String(limit), offset: String(offset) },
  });
  return parseJson(await res);
}

export type AdminCronSlackRoute = {
  teamId: string;
  channelId: string;
  threadTs: string;
};

export type AdminCronDelivery = {
  cronJobId: string;
  teamId: string | null;
  channelId: string | null;
  threadTs: string | null;
  deliveredAt: string;
};

export type AdminCronJobsResponse = {
  jobs: Record<string, unknown>[];
  slackRoutes: Record<string, AdminCronSlackRoute>;
  recentDeliveries: AdminCronDelivery[];
};

export async function getUserCronJobs(userId: string): Promise<AdminCronJobsResponse> {
  const res = await rpc.users[":id"]["cron-jobs"].$get({
    param: { id: userId },
  });
  return parseJson(await res);
}

export type ContactStatus = "pending" | "resolved";

export type ContactItem = {
  id: string;
  email: string;
  name: string | null;
  locale: string;
  status: ContactStatus;
  messagePreview: string;
  createdAt: string;
  resolvedAt: string | null;
  signupCountry: string | null;
};

export type ContactDetail = ContactItem & {
  message: string;
  signupIp: string | null;
  signupGeo: Record<string, string | undefined> | null;
  signupClient: Record<string, string | number | undefined> | null;
};

export type ContactList = {
  items: ContactItem[];
  total: number;
  limit: number;
  offset: number;
};

export async function getContactMessages(
  limit = 50,
  offset = 0,
  status?: ContactStatus,
): Promise<ContactList> {
  const query: Record<string, string> = {
    limit: String(limit),
    offset: String(offset),
  };
  if (status) query.status = status;
  const res = await rpc.contact.$get({ query });
  return parseJson(await res);
}

export async function getContactMessage(id: string): Promise<ContactDetail> {
  const res = await rpc.contact[":id"].$get({ param: { id } });
  return parseJson(await res);
}

export async function updateContactStatus(
  id: string,
  status: ContactStatus,
): Promise<ContactDetail> {
  const res = await rpc.contact[":id"].$patch({ param: { id }, json: { status } });
  return parseJson(await res);
}

export type NewsletterItem = {
  id: string;
  email: string;
  locale: string;
  source: string;
  createdAt: string;
  signupCountry: string | null;
};

export type NewsletterList = {
  items: NewsletterItem[];
  total: number;
  limit: number;
  offset: number;
};

export async function getNewsletterSubscribers(
  limit = 50,
  offset = 0,
): Promise<NewsletterList> {
  const res = await rpc.newsletter.$get({
    query: { limit: String(limit), offset: String(offset) },
  });
  return parseJson(await res);
}

export type AdminSpendUserResponse = z.infer<typeof AdminSpendUserResponseSchema>;
export type AdminSpendAggregateResponse = z.infer<typeof AdminSpendAggregateResponseSchema>;
export type AdminSpendDailyRow = AdminSpendUserResponse["rows"][number];

export async function getUserSpend(
  id: string,
  opts?: { from?: string; to?: string },
): Promise<AdminSpendUserResponse> {
  const query: Record<string, string> = {};
  if (opts?.from) query.from = opts.from;
  if (opts?.to) query.to = opts.to;
  const res = await rpc.spend.user[":id"].$get({ param: { id }, query });
  return parseJson(await res);
}

export type AdminAggregateGroupBy = "day" | "category" | "model" | "tool" | "user" | "country";

export interface AdminAggregateFilters {
  from?: string;
  to?: string;
  groupBy?: AdminAggregateGroupBy;
  category?: "llm" | "tools" | "infra";
  country?: string;
  hasVm?: "true" | "false";
  minCents?: number;
}

export async function getAggregateSpend(
  filters: AdminAggregateFilters = {},
): Promise<AdminSpendAggregateResponse> {
  const query: Record<string, string> = {};
  if (filters.from) query.from = filters.from;
  if (filters.to) query.to = filters.to;
  if (filters.groupBy) query.groupBy = filters.groupBy;
  if (filters.category) query.category = filters.category;
  if (filters.country) query.country = filters.country;
  if (filters.hasVm) query.hasVm = filters.hasVm;
  if (filters.minCents != null) query.minCents = String(filters.minCents);
  const res = await rpc.spend.aggregate.$get({ query });
  return parseJson(await res);
}

export type AdminRevenueAggregateResponse = z.infer<typeof AdminRevenueAggregateResponseSchema>;
export type AdminRevenuePaymentsResponse = z.infer<typeof AdminRevenuePaymentsResponseSchema>;
export type AdminSubscriptionAuditResponse = z.infer<typeof AdminSubscriptionAuditResponseSchema>;
export type AdminUserRevenueResponse = z.infer<typeof AdminUserRevenueResponseSchema>;
export type AdminCreditGrantsResponse = z.infer<typeof AdminCreditGrantsResponseSchema>;
export type AdminRevenuePayment = AdminRevenuePaymentsResponse["items"][number];
export type AdminSubscriptionAuditItem = AdminSubscriptionAuditResponse["items"][number];

export type AdminRevenueGroupBy = "day" | "type" | "plan" | "country" | "user";

export interface AdminRevenueFilters {
  from?: string;
  to?: string;
  type?: "subscription" | "topup";
  status?: "paid" | "failed" | "all";
  plan?: "solo" | "pro" | "team";
  country?: string;
  hasVat?: "yes" | "no" | "all";
  groupBy?: AdminRevenueGroupBy;
}

function revenueQuery(filters: AdminRevenueFilters): Record<string, string> {
  const query: Record<string, string> = {};
  if (filters.from) query.from = filters.from;
  if (filters.to) query.to = filters.to;
  if (filters.type) query.type = filters.type;
  if (filters.status) query.status = filters.status;
  if (filters.plan) query.plan = filters.plan;
  if (filters.country) query.country = filters.country;
  if (filters.hasVat) query.hasVat = filters.hasVat;
  if (filters.groupBy) query.groupBy = filters.groupBy;
  return query;
}

export async function getRevenueAggregate(
  filters: AdminRevenueFilters = {},
): Promise<AdminRevenueAggregateResponse> {
  const res = await rpc.revenue.aggregate.$get({ query: revenueQuery(filters) });
  return parseJson(await res);
}

export async function getRevenuePayments(
  filters: AdminRevenueFilters & { userId?: string; limit?: number; offset?: number } = {},
): Promise<AdminRevenuePaymentsResponse> {
  const query = revenueQuery(filters);
  if (filters.userId) query.userId = filters.userId;
  if (filters.limit != null) query.limit = String(filters.limit);
  if (filters.offset != null) query.offset = String(filters.offset);
  const res = await rpc.revenue.payments.$get({ query });
  return parseJson(await res);
}

export async function getSubscriptionAudit(filters: {
  from?: string;
  to?: string;
  userId?: string;
  eventType?: string;
  plan?: "solo" | "pro" | "team";
  limit?: number;
  offset?: number;
} = {}): Promise<AdminSubscriptionAuditResponse> {
  const query: Record<string, string> = {};
  if (filters.from) query.from = filters.from;
  if (filters.to) query.to = filters.to;
  if (filters.userId) query.userId = filters.userId;
  if (filters.eventType) query.eventType = filters.eventType;
  if (filters.plan) query.plan = filters.plan;
  if (filters.limit != null) query.limit = String(filters.limit);
  if (filters.offset != null) query.offset = String(filters.offset);
  const res = await rpc.revenue["subscription-audit"].$get({ query });
  return parseJson(await res);
}

export async function getUserRevenue(
  id: string,
  opts?: { paidFrom?: string; paidTo?: string },
): Promise<AdminUserRevenueResponse> {
  const query: Record<string, string> = {};
  if (opts?.paidFrom) query.paidFrom = opts.paidFrom;
  if (opts?.paidTo) query.paidTo = opts.paidTo;
  const res = await rpc.revenue.user[":id"].$get({ param: { id }, query });
  return parseJson(await res);
}

export async function getUserRevenuePayments(
  id: string,
  opts?: { from?: string; to?: string; limit?: number; offset?: number },
): Promise<AdminRevenuePaymentsResponse> {
  const query: Record<string, string> = {};
  if (opts?.from) query.from = opts.from;
  if (opts?.to) query.to = opts.to;
  if (opts?.limit != null) query.limit = String(opts.limit);
  if (opts?.offset != null) query.offset = String(opts.offset);
  const res = await rpc.revenue.user[":id"].payments.$get({ param: { id }, query });
  return parseJson(await res);
}

export async function getUserCreditGrants(
  id: string,
  opts?: { limit?: number; offset?: number },
): Promise<AdminCreditGrantsResponse> {
  const query: Record<string, string> = {};
  if (opts?.limit != null) query.limit = String(opts.limit);
  if (opts?.offset != null) query.offset = String(opts.offset);
  const res = await rpc.revenue.user[":id"]["credit-grants"].$get({ param: { id }, query });
  return parseJson(await res);
}

export async function getUserSubscriptionAudit(
  id: string,
  opts?: { limit?: number; offset?: number },
): Promise<AdminSubscriptionAuditResponse> {
  const query: Record<string, string> = {};
  if (opts?.limit != null) query.limit = String(opts.limit);
  if (opts?.offset != null) query.offset = String(opts.offset);
  const res = await rpc.revenue.user[":id"]["subscription-audit"].$get({ param: { id }, query });
  return parseJson(await res);
}

export function invoicePdfUrl(paymentId: string): string {
  return `/api/revenue/payments/${paymentId}/invoice.pdf`;
}

export type AdminReconcileProvider = z.infer<typeof ReconcileProvider>;
export type AdminReconcileSeverity = z.infer<typeof ReconcileSeverity>;
export type AdminReconcileResponse = z.infer<typeof AdminReconcileResponseSchema>;
export type AdminReconcileFinding = AdminReconcileResponse["findings"][number];
export type AdminReconcileRun = AdminReconcileResponse["runs"][number];
export type AdminPersistentGhost = AdminReconcileResponse["persistent_ghosts"][number];

export interface AdminReconcileFilters {
  provider?: AdminReconcileProvider;
  severity?: AdminReconcileSeverity;
  userId?: string;
}

export async function getReconciliation(
  filters: AdminReconcileFilters = {},
): Promise<AdminReconcileResponse> {
  const query: Record<string, string> = {};
  if (filters.provider) query.provider = filters.provider;
  if (filters.severity) query.severity = filters.severity;
  if (filters.userId) query.userId = filters.userId;
  const res = await rpc.ops.reconcile.$get({ query });
  return parseJson(await res);
}

export async function refreshReconciliation(): Promise<
  z.infer<typeof AdminReconcileRefreshResponseSchema>
> {
  const res = await rpc.ops.reconcile.refresh.$post();
  return parseJson(await res);
}

export async function deletePersistentGhost(
  serverId: number,
  userId: string,
): Promise<{ ok: true; hetzner_server_id: number; user_id: string }> {
  const res = await rpc.ops.hetzner.ghosts[":serverId"].delete.$post({
    param: { serverId: String(serverId) },
    json: { userId },
  });
  return parseJson(await res);
}

export type SlackQueueStats = z.infer<typeof AdminSlackQueueResponseSchema>;

export async function getSlackQueue(): Promise<SlackQueueStats> {
  const res = await rpc.ops["slack-queue"].$get();
  return parseJson(await res);
}

export type SlackJobRow = z.infer<typeof AdminSlackJobsResponseSchema>["jobs"][number];

export async function getSlackJobs(limit = 20): Promise<SlackJobRow[]> {
  const res = await rpc.ops["slack-jobs"].$get({ query: { limit: String(limit) } });
  const body = await parseJson<{ jobs: SlackJobRow[] }>(await res);
  return body.jobs;
}

export type MysqlTablesStats = z.infer<typeof AdminMysqlTablesResponseSchema>;

export async function getMysqlTables(): Promise<MysqlTablesStats> {
  const res = await rpc.ops["mysql-tables"].$get();
  return parseJson(await res);
}

export type AdminOpsIncident = z.infer<
  typeof AdminOpsIncidentsResponseSchema
>["incidents"][number];
export type AdminOpsIncidentDetail = z.infer<typeof AdminOpsIncidentDetailResponseSchema>;
export type AdminOpsIncidentStatus = z.infer<typeof OpsIncidentStatus>;
export type AdminOpsIncidentSeverity = z.infer<typeof OpsIncidentSeverity>;
export type AdminOpsIncidentSource = z.infer<typeof OpsIncidentSource>;

export interface AdminOpsIncidentFilters {
  status?: AdminOpsIncidentStatus;
  source?: AdminOpsIncidentSource;
  severity?: AdminOpsIncidentSeverity;
  userId?: string;
  limit?: number;
  offset?: number;
}

export async function getOpsIncidents(
  filters: AdminOpsIncidentFilters = {},
): Promise<z.infer<typeof AdminOpsIncidentsResponseSchema>> {
  const query: Record<string, string> = {};
  if (filters.status) query.status = filters.status;
  if (filters.source) query.source = filters.source;
  if (filters.severity) query.severity = filters.severity;
  if (filters.userId) query.userId = filters.userId;
  if (filters.limit != null) query.limit = String(filters.limit);
  if (filters.offset != null) query.offset = String(filters.offset);
  const res = await rpc.ops.incidents.$get({ query });
  return parseJson(await res);
}

export async function getOpsIncident(id: string): Promise<AdminOpsIncidentDetail> {
  const res = await rpc.ops.incidents[":id"].$get({ param: { id } });
  return parseJson(await res);
}

export async function patchOpsIncident(
  id: string,
  body: { status?: AdminOpsIncidentStatus; resolutionNotes?: string },
): Promise<AdminOpsIncidentDetail> {
  const res = await rpc.ops.incidents[":id"].$patch({ param: { id }, json: body });
  return parseJson(await res);
}

export async function addOpsIncidentNote(id: string, body: string): Promise<AdminOpsIncidentDetail> {
  const res = await rpc.ops.incidents[":id"].notes.$post({ param: { id }, json: { body } });
  return parseJson(await res);
}

export type AdminConversationSessionSummary = z.infer<
  typeof AdminConversationSessionSummarySchema
>;
export type AdminConversationMessage = z.infer<typeof AdminConversationMessageSchema>;
export type AdminConversationDetail = z.infer<typeof AdminConversationDetailResponseSchema>;
export type AdminConversationList = z.infer<typeof AdminConversationListResponseSchema>;

export async function getConversations(opts?: {
  userId?: string;
  limit?: number;
  offset?: number;
}): Promise<AdminConversationList> {
  const query: Record<string, string> = {
    limit: String(opts?.limit ?? 25),
    offset: String(opts?.offset ?? 0),
  };
  if (opts?.userId) query.userId = opts.userId;
  const res = await rpc.conversations.$get({ query });
  return parseJson(await res);
}

export async function getConversation(sessionId: string): Promise<AdminConversationDetail> {
  const res = await rpc.conversations[":sessionId"].$get({ param: { sessionId } });
  return parseJson(await res);
}

export type HireCodeSummary = {
  id: string;
  code: string;
  codeType: "single" | "multi";
  maxRedemptions: number | null;
  redemptionCount: number;
  status: "active" | "revoked" | "exhausted";
  issuerType: "admin" | "user";
  isPersonalReferral: boolean;
  campaignTag: string | null;
  note: string | null;
  expiresAt: string | null;
  createdAt: string;
  revokedAt: string | null;
  sharePath: string;
};

export type HireCodeList = {
  items: HireCodeSummary[];
  total: number;
  limit: number;
  offset: number;
};

export type HireCodeDetail = HireCodeSummary & {
  issuerAdminId: string | null;
  issuerUserId: string | null;
  redemptions: {
    id: string;
    userId: string;
    userEmail: string;
    redeemedAt: string;
  }[];
};

export async function getHireCodes(opts?: {
  limit?: number;
  offset?: number;
  status?: "active" | "revoked" | "exhausted";
  type?: "single" | "multi";
  campaign?: string;
  personal?: boolean;
  search?: string;
}): Promise<HireCodeList> {
  const query: Record<string, string> = {
    limit: String(opts?.limit ?? 50),
    offset: String(opts?.offset ?? 0),
  };
  if (opts?.status) query.status = opts.status;
  if (opts?.type) query.type = opts.type;
  if (opts?.campaign) query.campaign = opts.campaign;
  if (opts?.personal !== undefined) query.personal = String(opts.personal);
  if (opts?.search) query.search = opts.search;
  const res = await rpc.hire.codes.$get({ query });
  return parseJson(await res);
}

export async function createHireCode(body: {
  codeType: "single" | "multi";
  maxRedemptions?: number | null;
  campaignTag?: string;
  note?: string;
  expiresAt?: string | null;
}): Promise<{ id: string; code: string; sharePath: string }> {
  const res = await rpc.hire.codes.$post({ json: body });
  return parseJson(await res);
}

export async function getHireCode(id: string): Promise<HireCodeDetail> {
  const res = await rpc.hire.codes[":id"].$get({ param: { id } });
  return parseJson(await res);
}

export async function revokeHireCode(id: string): Promise<{ ok: true }> {
  const res = await rpc.hire.codes[":id"].revoke.$post({ param: { id } });
  return parseJson(await res);
}

export async function reissuePersonalHireCode(userId: string): Promise<{
  id: string;
  code: string;
  sharePath: string;
}> {
  const res = await rpc.hire.codes["reissue-personal"].$post({ json: { userId } });
  return parseJson(await res);
}

export type WithheldReward = {
  id: string;
  referrerUserId: string;
  referrerEmail: string;
  refereeUserId: string;
  refereeEmail: string;
  referrerCredits: number;
  refereeCredits: number;
  withheldReason: string | null;
  paymentRef: string | null;
  createdAt: string;
};

export async function getWithheldRewards(): Promise<{ items: WithheldReward[]; total: number }> {
  const res = await rpc.hire.rewards.withheld.$get();
  return parseJson(await res);
}

export async function approveWithheldReward(id: string): Promise<{ ok: true }> {
  const res = await rpc.hire.rewards[":id"].approve.$post({ param: { id } });
  return parseJson(await res);
}

export async function denyWithheldReward(id: string): Promise<{ ok: true }> {
  const res = await rpc.hire.rewards[":id"].deny.$post({ param: { id } });
  return parseJson(await res);
}

